import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { ArrowLeft, Save, ArrowRightLeft, Loader2, Upload, FileText, CheckCircle, XCircle, Clock, Lock, Download, Trash2, Eye, Mail, Link2, MessageCircle, Send, User, Users, ChevronLeft, ChevronRight, Scissors, Image as ImageIcon, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import CounselorTab from '../components/desks/CounselorTab';
import AdmissionTab from '../components/desks/AdmissionTab';
import ComplianceTab from '../components/desks/ComplianceTab';
import VisaTab from '../components/desks/VisaTab';
import DeskShiftDialog from '../components/DeskShiftDialog';
import DocumentPreviewButton from '../components/DocumentPreviewButton';

const ApplicationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, user } = useAuth();
  
  // Get the return tab, page, and limit from URL params for back navigation
  const returnTab = searchParams.get('tab') || 'fresh_leads';
  const returnPage = searchParams.get('page') || '1';
  const returnLimit = searchParams.get('limit') || '20';
  
  // Helper function to navigate back preserving tab, page, and records per page
  const goBack = () => {
    const pageParam = returnPage !== '1' ? `&page=${returnPage}` : '';
    const limitParam = returnLimit !== '20' ? `&limit=${returnLimit}` : '';
    navigate(`/applications?tab=${returnTab}${pageParam}${limitParam}`);
  };
  
  const [application, setApplication] = useState(null);
  const [desks, setDesks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentTypesMetadata, setDocumentTypesMetadata] = useState({
    countryName: null,
    degreeTypeName: null,
    hasRequiredDocs: false,
    totalRequired: 0
  });
  const [allMasterDataDocTypes, setAllMasterDataDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [activeTab, setActiveTab] = useState('counselor');
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [targetDesk, setTargetDesk] = useState('');
  const [shiftNotes, setShiftNotes] = useState('');
  const [shiftToUser, setShiftToUser] = useState('');  // Selected user to assign
  const [shiftToRole, setShiftToRole] = useState('');  // Selected role
  const [teams, setTeams] = useState([]);  // All teams
  const [roles, setRoles] = useState([]);  // All roles
  const [activityLogs, setActivityLogs] = useState([]);
  const [formData, setFormData] = useState({});
  const [masterData, setMasterData] = useState({
    countries: [], universities: [], programs: [], users: [], agents: [], leadStatuses: [], applicationStatuses: [], offerStatuses: [], applicationTypes: []
  });
  
  // Additional Documents state
  const [additionalDocName, setAdditionalDocName] = useState('');
  const [additionalDocUploading, setAdditionalDocUploading] = useState(false);
  const additionalDocInputRef = React.useRef(null);
  
  // ZIP Upload state
  const [zipUploading, setZipUploading] = useState(false);
  const zipInputRef = React.useRef(null);

  // ZIP Download state — "Download All" button in Documents tab
  const [zipDownloading, setZipDownloading] = useState(false);
  
  // Document reassignment state
  const [reassigningDocId, setReassigningDocId] = useState(null);

  // Navigation state for Previous/Next
  const [neighbors, setNeighbors] = useState({ previous_id: null, next_id: null, current_index: 0, total: 0 });

  // Agent Chat state
  const [agentChatOpen, setAgentChatOpen] = useState(false);
  const [agentChatMessages, setAgentChatMessages] = useState([]);
  const [agentChatInput, setAgentChatInput] = useState('');
  const [agentChatSending, setAgentChatSending] = useState(false);
  const [agentInfo, setAgentInfo] = useState(null);
  const agentChatEndRef = React.useRef(null);
  
  // Document Gallery state
  const [docGalleryOpen, setDocGalleryOpen] = useState(false);
  const [docGalleryTargetType, setDocGalleryTargetType] = useState(null); // The document type slot we're filling
  const [selectedGalleryDoc, setSelectedGalleryDoc] = useState(null);
  const [attachingFromGallery, setAttachingFromGallery] = useState(false);
  
  // Replace Document Modal state
  const [replaceDocModalOpen, setReplaceDocModalOpen] = useState(false);
  const [replaceDocTargetType, setReplaceDocTargetType] = useState(null); // Document type slot to replace
  const [replaceDocCurrent, setReplaceDocCurrent] = useState(null); // Currently assigned document
  const [selectedReplaceDoc, setSelectedReplaceDoc] = useState(null);
  const [replacingDocument, setReplacingDocument] = useState(false);
  
  // PDF Split state
  const [splittingPdfId, setSplittingPdfId] = useState(null);

  // Document hover preview state
  const [previewUrls, setPreviewUrls] = useState({});
  const [loadingPreviews, setLoadingPreviews] = useState({});

  // Fetch document preview URL for hover
  const fetchDocumentPreview = async (docId, filename = '') => {
    if (previewUrls[docId] || loadingPreviews[docId]) return;
    
    setLoadingPreviews(prev => ({ ...prev, [docId]: true }));
    try {
      // Use the new /preview endpoint which handles both images and PDFs
      const response = await api().get(`/documents/${docId}/preview`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrls(prev => ({ ...prev, [docId]: { url, contentType: response.headers['content-type'] } }));
    } catch (error) {
      console.error('Failed to fetch preview:', error);
      // Mark as failed so we don't retry
      setPreviewUrls(prev => ({ ...prev, [docId]: { url: null, failed: true } }));
    } finally {
      setLoadingPreviews(prev => ({ ...prev, [docId]: false }));
    }
  };

  // Document hover preview component
  const DocumentHoverPreview = ({ doc, children }) => {
    const filename = doc.file_name || doc.filename || '';
    const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(filename);
    const isPdf = /\.pdf$/i.test(filename);
    const preview = previewUrls[doc.id];
    
    return (
      <HoverCard openDelay={300} closeDelay={100}>
        <HoverCardTrigger asChild onMouseEnter={() => fetchDocumentPreview(doc.id)}>
          {children}
        </HoverCardTrigger>
        <HoverCardContent side="right" align="start" className="w-72 p-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2 pb-2 border-b">
              {isImage ? <ImageIcon className="w-4 h-4 text-primary" /> : <FileText className="w-4 h-4 text-primary" />}
              <span className="text-sm font-medium truncate">{filename}</span>
            </div>
            <div className="relative min-h-[120px] bg-gray-50 rounded-md flex items-center justify-center overflow-hidden">
              {loadingPreviews[doc.id] ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Loading preview...</span>
                </div>
              ) : preview ? (
                isImage ? (
                  <img 
                    src={preview.url} 
                    alt={filename} 
                    className="max-w-full max-h-[200px] object-contain rounded"
                  />
                ) : isPdf ? (
                  <iframe
                    src={preview.url}
                    title={filename}
                    className="w-full h-[200px] border-0 rounded"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 p-4 text-center">
                    <FileText className="w-10 h-10 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Preview not available for this file type</span>
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center gap-2 p-4">
                  <Eye className="w-8 h-8 text-muted-foreground/50" />
                  <span className="text-xs text-muted-foreground">Hover to load preview</span>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground text-center">
              {doc.document_type || doc.detected_type || 'Document'} • {doc.file_type || filename.split('.').pop()}
            </p>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  // Split PDF into individual pages
  const handleSplitPdf = async (docId, filename) => {
    if (!filename?.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files can be split');
      return;
    }
    
    setSplittingPdfId(docId);
    try {
      const response = await api().post(`/documents/${docId}/split-pdf`);
      if (response.data.success) {
        toast.success(`Split PDF into ${response.data.documents_created} separate pages`);
        fetchDocuments(); // Refresh document list
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to split PDF';
      toast.error(errorMsg);
    } finally {
      setSplittingPdfId(null);
    }
  };

  // Send welcome emails to student
  const sendWelcomeEmails = async () => {
    if (!application?.student_email) {
      toast.error('No student email available');
      return;
    }
    setSendingEmails(true);
    try {
      const response = await api().post(`/applications/${id}/send-welcome-emails`);
      if (response.data.welcome_email?.success) {
        toast.success('Welcome email sent successfully!');
      } else if (response.data.welcome_email?.reason) {
        toast.error(`Welcome email failed: ${response.data.welcome_email.reason}`);
      }
      if (response.data.document_request_email?.success) {
        toast.success('Document request email sent!');
      }
      // Refresh application to get updated token if generated
      fetchApplication();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingEmails(false);
    }
  };

  // Get user's desk permissions from roles
  const getUserDeskPermissions = () => {
    // Check if user is admin via direct flags first
    if (user?.is_admin || user?.is_vendor_admin || user?.user_type === 'vendor_admin') {
      return ['*'];
    }
    
    if (!user?.roles) return [];
    const permissions = [];
    let hasApplicationUpdate = false;
    
    user.roles.forEach(role => {
      // Check if role name is Admin (case insensitive)
      if (role.name && role.name.toLowerCase() === 'admin') {
        permissions.push('*');
        return;
      }
      
      if (role.permissions) {
        role.permissions.forEach(perm => {
          if (perm === '*') {
            // Admin has access to all desks
            permissions.push('*');
          } else if (perm.startsWith('desk:')) {
            permissions.push(perm.replace('desk:', ''));
          } else if (perm === 'applications:update' || perm === 'applications:edit' || perm === 'applications:*') {
            // User has application update permission - can edit all desks
            hasApplicationUpdate = true;
          }
        });
      }
      
      // Also check module_permissions if present (newer format)
      if (role.module_permissions) {
        const appPerms = role.module_permissions.applications || [];
        if (appPerms.includes('update') || appPerms.includes('edit') || appPerms.includes('*')) {
          hasApplicationUpdate = true;
        }
      }
    });
    
    // If user has applications:update permission, they can edit all desks
    if (hasApplicationUpdate && !permissions.includes('*')) {
      permissions.push('*');
    }
    
    return permissions;
  };

  const userDeskPermissions = getUserDeskPermissions();
  const isAdmin = userDeskPermissions.includes('*');
  const isAgent = (user?.user_type === 'agent');

  // Agent's designated counselor — used to auto-target the Shift Desk button
  // so agents don't have to pick anyone manually. Fetched lazily.
  const [agentAssignedCounselor, setAgentAssignedCounselor] = useState(null);
  useEffect(() => {
    if (!isAgent) return;
    api().get('/agent-portal/me/assigned-counselor')
      .then((res) => setAgentAssignedCounselor(res.data?.counselor || null))
      .catch(() => setAgentAssignedCounselor(null));
  }, [isAgent]);

  // For external agents, restrict to only 3 tabs and rename them:
  //   counselor  → "Applicant Profile"
  //   admission  → "Program Details"
  //   document   → "Documents"
  const AGENT_TAB_LABELS = {
    counselor: 'Applicant Profile',
    admission: 'Program Details',
    document: 'Documents',
  };
  const agentVisibleSlugs = new Set(['counselor', 'admission', 'document']);
  const visibleDesks = React.useMemo(() => {
    if (!isAgent) return desks;
    return desks
      .filter((d) => agentVisibleSlugs.has(d.slug))
      .map((d) => ({ ...d, name: AGENT_TAB_LABELS[d.slug] || d.name }));
  }, [desks, isAgent]);

  // Check if user can edit a specific desk
  const canEditDesk = (deskSlug) => {
    if (isAdmin) return true;
    // External agents can edit the 3 tabs they see (applicant profile,
    // program details, documents) on their own applications.
    if (isAgent && ['counselor', 'admission', 'document'].includes(deskSlug)) return true;
    return userDeskPermissions.includes(deskSlug);
  };

  // Helper function to render desk options
  const renderDeskOptions = () => {
    const filteredDesks = desks.filter(d => d.slug !== application?.current_desk);
    const items = [];
    for (let i = 0; i < filteredDesks.length; i++) {
      const desk = filteredDesks[i];
      items.push(<SelectItem key={desk.id} value={desk.slug}>{desk.name}</SelectItem>);
    }
    return items;
  };

  // Helper function to render desk tabs
  const renderDeskTabs = () => {
    const items = [];
    for (let i = 0; i < desks.length; i++) {
      const desk = desks[i];
      const isActive = activeTab === desk.slug;
      items.push(
        <TabsTrigger 
          key={desk.id} 
          value={desk.slug} 
          className={`${isActive ? getDeskColor(desk.slug) : ''}`}
          data-testid={`tab-${desk.slug}`}
        >
          {desk.name}
        </TabsTrigger>
      );
    }
    return items;
  };

  // Helper function to render desk tab triggers with lock
  const renderDeskTabTriggers = () => {
    const items = [];
    const list = visibleDesks;
    for (let i = 0; i < list.length; i++) {
      const desk = list[i];
      const isLocked = !hasTabAccess(desk.slug);
      const isCurrentDesk = desk.slug === application?.current_desk;
      items.push(
        <TabsTrigger 
          key={desk.id} 
          value={desk.slug} 
          disabled={isLocked} 
          className={`gap-2 ${isCurrentDesk ? 'ring-2 ring-primary ring-offset-2' : ''}`} 
          data-testid={`tab-${desk.slug}`}
        >
          {isLocked && <Lock className="w-3 h-3" />}
          {desk.name}
        </TabsTrigger>
      );
    }
    return items;
  };

  useEffect(() => {
    fetchApplication();
    fetchDesks();
    fetchTeamsAndRoles();
    fetchDocuments();
    fetchActivityLogs();
    fetchMasterData();
    fetchNeighbors();
  }, [id]);

  // Fetch neighbors for navigation
  const fetchNeighbors = async () => {
    try {
      const response = await api().get(`/applications/${id}/neighbors?tab=${returnTab}`);
      setNeighbors(response.data);
    } catch (error) {
      console.error('Failed to fetch neighbors:', error);
    }
  };
  
  // Re-fetch document types when application data is available (to get degree-specific docs)
  useEffect(() => {
    if (application?.id) {
      fetchDocumentTypes();
    }
  }, [application?.id, application?.admission?.university, application?.admission?.degree_type, application?.counselor?.nationality]);

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const fetchApplication = async (setTab = true) => {
    try {
      const response = await api().get(`/applications/${id}`);
      setApplication(response.data);
      
      // Merge top-level application fields into desk_data.counselor
      // This handles agent-submitted applications where data is at root level
      const deskData = response.data.desk_data || {};
      const counselorData = deskData.counselor || {};
      
      // Map top-level fields to counselor desk structure
      const mergedCounselor = {
        ...counselorData,
        // Personal Info
        title: counselorData.title || response.data.title,
        first_name: counselorData.first_name || response.data.first_name,
        last_name: counselorData.last_name || response.data.last_name,
        gender: counselorData.gender || response.data.gender,
        dob: counselorData.dob || counselorData.date_of_birth || response.data.date_of_birth,
        email: counselorData.email || response.data.student_email,
        telephone_1: counselorData.telephone_1 || counselorData.phone || response.data.student_phone,
        telephone_2: counselorData.telephone_2 || counselorData.phone_2 || response.data.student_phone_2,
        nationality: counselorData.nationality || response.data.nationality,
        // ID Documents - map to CounselorTab expected field names
        passport: counselorData.passport || counselorData.passport_number || response.data.passport_number,
        passport_issue_date: counselorData.passport_issue_date || response.data.passport_issue_date,
        passport_expiry_date: counselorData.passport_expiry || counselorData.passport_expiry_date || response.data.passport_expiry_date,
        nic: counselorData.nic || counselorData.nic_number || response.data.nic_number,
        nic_issue_date: counselorData.nic_issue_date || response.data.nic_issue_date,
        nic_expiry_date: counselorData.nic_expiry || counselorData.nic_expiry_date || response.data.nic_expiry_date,
        // Address
        address: counselorData.address || response.data.address,
        postal_code: counselorData.postal_code || response.data.postal_code,
        country_living: counselorData.country_living || response.data.country_living_in || response.data.country,
        province: counselorData.province || response.data.province,
        city: counselorData.city || response.data.city,
        // Academic
        last_education_country: counselorData.last_education_country || response.data.last_education_country,
        last_qualification: counselorData.last_qualification || response.data.last_qualification,
        qualification_status: counselorData.qualification_status || response.data.qualification_status,
        last_qualification_subject: counselorData.last_qualification_subject || response.data.last_qualification_subject,
        last_qualification_faculty: counselorData.last_qualification_faculty || response.data.last_qualification_faculty,
        last_qualification_grades: counselorData.last_qualification_grades || response.data.last_qualification_grades,
        last_qualification_year: counselorData.last_qualification_year || response.data.last_qualification_year,
        last_institution: counselorData.last_institution || response.data.last_institution,
        last_institution_city: counselorData.last_institution_city || response.data.last_institution_city,
        // English Test
        english_test_type: counselorData.english_test_type || response.data.english_test_type,
        english_test_score: counselorData.english_test_score || response.data.english_test_score,
        // Notes
        notes: counselorData.notes || response.data.notes
      };
      
      // Also map admission data if present at top level
      const admissionData = deskData.admission || {};
      const mergedAdmission = {
        ...admissionData,
        // Map university to both fields used in admission tab
        partner_university: admissionData.partner_university || response.data.university_id,
        university: admissionData.university || response.data.university_id,
        degree_type: admissionData.degree_type || response.data.degree_type,
        program: admissionData.program || response.data.program_id,
        intake: admissionData.intake || response.data.intake_id,
        campus: admissionData.campus || response.data.campus_id
      };
      
      setFormData({
        ...deskData,
        counselor: mergedCounselor,
        admission: mergedAdmission
      });
      
      // Only set activeTab on initial load, not on refreshes after shift
      if (setTab && isInitialLoad) {
        setActiveTab(response.data.current_desk?.toLowerCase() || 'counselor');
        setIsInitialLoad(false);
      }
    } catch (error) {
      toast.error('Failed to load application');
      goBack();
    } finally {
      setLoading(false);
    }
  };

  const fetchDesks = async () => {
    try { const response = await api().get('/desks'); setDesks(response.data); } catch (error) { console.error('Failed to load desks'); }
  };

  const fetchTeamsAndRoles = async () => {
    try {
      const [teamsRes, rolesRes] = await Promise.all([
        api().get('/teams'),
        api().get('/roles')
      ]);
      setTeams(teamsRes.data || []);
      setRoles(rolesRes.data || []);
    } catch (error) {
      console.error('Failed to load teams/roles:', error);
    }
  };

  const fetchDocuments = async () => {
    try { 
      const response = await api().get(`/documents?application_id=${id}`); 
      setDocuments(response.data);
      // Clear preview cache when documents are refreshed to ensure fresh previews
      setPreviewUrls({});
      setLoadingPreviews({});
    } catch (error) { console.error('Failed to load documents'); }
  };

  const fetchDocumentTypes = async () => {
    try { 
      // First get basic document types from master data
      const response = await api().get('/master-data?type=document_type'); 
      const masterDataDocTypes = response.data || [];
      
      // Create a list for required docs (from Core Settings based on country + degree)
      let requiredDocs = [];
      let countryName = null;
      let degreeTypeName = null;
      
      // If we have application data, get the required docs for this specific country + degree combination
      const nationalityId = application?.desk_data?.counselor?.nationality || application?.desk_data?.counselor?.country_living;
      const degreeTypeId = application?.desk_data?.admission?.degree_type;
      const universityId = application?.desk_data?.admission?.partner_university || application?.desk_data?.admission?.university;
      
      if (universityId && nationalityId && degreeTypeId) {
        try {
          // Get country name from nationality
          const countryRes = await api().get(`/master-data/${nationalityId}`);
          countryName = countryRes.data?.name;
          
          // Get degree type NAME - handle both UUID and name formats
          degreeTypeName = degreeTypeId;
          if (degreeTypeId && degreeTypeId.length === 36 && degreeTypeId.includes('-')) {
            try {
              const degreeTypeRes = await api().get(`/master-data/${degreeTypeId}`);
              degreeTypeName = degreeTypeRes.data?.name || degreeTypeId;
            } catch (err) {
              console.log('Could not resolve degree type UUID:', err);
            }
          }
          
          if (countryName && degreeTypeName) {
            const reqDocsRes = await api().get(
              `/university-required-docs/${universityId}/country/${countryName}?degree_type=${encodeURIComponent(degreeTypeName)}`
            );
            requiredDocs = reqDocsRes.data?.required_documents || [];
          }
        } catch (err) {
          console.log('Could not fetch degree-specific docs:', err);
        }
      }
      
      // Build the final document types list
      // Priority: Required docs from Core Settings, then any additional master data doc types
      let finalDocTypes = [];
      
      // Add required docs first (from Core Settings)
      requiredDocs.forEach(docName => {
        // Try to find matching master data entry for this doc type
        const masterMatch = masterDataDocTypes.find(
          dt => dt.name.toLowerCase() === docName.toLowerCase()
        );
        
        finalDocTypes.push({
          id: masterMatch?.id || `req_${docName.replace(/\s+/g, '_').toLowerCase()}`,
          name: docName,
          type: 'document_type',
          isRequired: true,
          fromCoreSettings: true
        });
      });
      
      // Store metadata about the configuration
      setDocumentTypesMetadata({
        countryName,
        degreeTypeName,
        hasRequiredDocs: requiredDocs.length > 0,
        totalRequired: requiredDocs.length
      });
      
      setDocumentTypes(finalDocTypes);
      setAllMasterDataDocTypes(masterDataDocTypes); // Keep all master data doc types for additional docs
    } catch (error) { 
      console.error('Failed to load document types'); 
    }
  };

  const fetchActivityLogs = async () => {
    try { const response = await api().get(`/activity-logs?entity_type=application&entity_id=${id}`); setActivityLogs(response.data.items || []); } catch (error) { console.error('Failed to load activity logs'); }
  };

  const fetchMasterData = async () => {
    try {
      const [countries, universities, programs, users, agents, leadStatuses, applicationStatuses, offerStatuses, applicationTypes] = await Promise.all([
        api().get('/master-data?type=country'), api().get('/master-data?type=university'), api().get('/master-data?type=program'),
        api().get('/users'), api().get('/agents'), api().get('/master-data?type=lead_status'),
        api().get('/master-data?type=application_status'), api().get('/master-data?type=offer_status'),
        api().get('/master-data?type=application_type')
      ]);
      setMasterData({ 
        countries: countries.data, 
        universities: universities.data, 
        programs: programs.data, 
        users: users.data, 
        agents: agents.data, 
        leadStatuses: leadStatuses.data, 
        applicationStatuses: applicationStatuses.data, 
        offerStatuses: offerStatuses.data,
        applicationTypes: applicationTypes.data
      });
    } catch (error) { console.error('Failed to load master data'); }
  };

  const handleDocumentDownload = async (docId, filename) => {
    try {
      const response = await api().get(`/documents/${docId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && detail.includes('re-upload')) {
        toast.error('Document file not found. Please ask the student to re-upload this document.');
      } else {
        toast.error('Failed to download document');
      }
    }
  };

  const handleDocumentView = async (docId, filename) => {
    try {
      const response = await api().get(`/documents/${docId}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && detail.includes('re-upload')) {
        toast.error('Document file not found. Please ask the student to re-upload this document.');
      } else {
        toast.error('Failed to view document');
      }
    }
  };

  const handleDocumentDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await api().delete(`/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const downloadAllDocumentsAsZip = async () => {
    if (documents.length === 0) {
      toast.error('No documents to download yet');
      return;
    }
    setZipDownloading(true);
    try {
      const response = await api().get(
        `/applications/${id}/documents/download-zip`,
        { responseType: 'blob' }
      );
      // Try to lift the filename from Content-Disposition; fall back to APP-id
      let filename = `application_${id?.slice(0, 8) || 'documents'}.zip`;
      const cd = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
      if (cd) {
        const m = /filename="?([^"]+)"?/.exec(cd);
        if (m) filename = m[1];
      }
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${documents.length} documents`);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to download documents';
      toast.error(detail);
    } finally {
      setZipDownloading(false);
    }
  };

  // Unlink (not delete) a document from a Required Document slot.
  // The file stays in AI Documents so the AI scanner can still see it.
  const handleRequiredDocUnlink = async (docId) => {
    if (!window.confirm("Remove this file from the required-document slot?\n\nThe file will stay in AI Documents — it won't be deleted.")) return;
    try {
      await api().put(`/documents/${docId}/reassign`, { document_type_id: null });
      toast.success('Removed from required slot — still available in AI Documents');
      fetchDocuments();
    } catch (error) {
      console.error('Unlink error:', error);
      toast.error(error.response?.data?.detail || 'Failed to unlink document');
    }
  };

  // Open document gallery modal to select from existing uploads
  const openDocumentGallery = (docType) => {
    setDocGalleryTargetType(docType);
    setSelectedGalleryDoc(null);
    setDocGalleryOpen(true);
  };

  // Get all available documents for the gallery (unassigned + all uploaded)
  const getGalleryDocuments = () => {
    // Get all documents that are NOT already assigned to a required document type
    const assignedDocIds = documents
      .filter(d => documentTypes.some(dt => d.document_type_id === dt.id))
      .map(d => d.id);
    
    // Return all documents that are not assigned to a required slot
    return documents.filter(d => !assignedDocIds.includes(d.id) || d.document_type_id !== docGalleryTargetType?.id);
  };

  // Attach selected document from gallery to the document type slot
  const attachDocumentFromGallery = async () => {
    if (!selectedGalleryDoc || !docGalleryTargetType) return;
    
    setAttachingFromGallery(true);
    try {
      // Use the reassign endpoint to assign the document to the required doc type slot
      await api().put(`/documents/${selectedGalleryDoc.id}/reassign`, {
        document_type_id: docGalleryTargetType.id
      });
      
      toast.success(`Document attached to ${docGalleryTargetType.name}`);
      setDocGalleryOpen(false);
      setSelectedGalleryDoc(null);
      setDocGalleryTargetType(null);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to attach document');
      console.error('Attach document error:', error);
    } finally {
      setAttachingFromGallery(false);
    }
  };

  // Open Replace Document Modal
  const openReplaceDocModal = async (docType, currentDoc) => {
    setReplaceDocTargetType(docType);
    setReplaceDocCurrent(currentDoc);
    setSelectedReplaceDoc(null);
    setReplaceDocModalOpen(true);
    
    // Eagerly fetch previews for all documents (both images AND PDFs)
    const docsToPreview = documents.filter(d => d.id !== currentDoc?.id);
    for (const doc of docsToPreview) {
      const filename = doc.file_name || doc.filename || '';
      const isPreviewable = filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|pdf)$/);
      if (isPreviewable && !previewUrls[doc.id]) {
        fetchDocumentPreview(doc.id, filename);
      }
    }
    // Also fetch preview for current doc
    if (currentDoc && !previewUrls[currentDoc.id]) {
      fetchDocumentPreview(currentDoc.id);
    }
  };

  // Get all available documents for replacement (excluding current)
  const getReplaceableDocuments = () => {
    if (!replaceDocCurrent) return documents;
    return documents.filter(d => d.id !== replaceDocCurrent.id);
  };

  // Replace document - detach current and attach selected
  const replaceDocument = async () => {
    if (!selectedReplaceDoc || !replaceDocTargetType) return;
    
    setReplacingDocument(true);
    try {
      // First, unassign the current document (set its document_type_id to null)
      if (replaceDocCurrent) {
        console.log('Unassigning current doc:', replaceDocCurrent.id);
        const unassignRes = await api().put(`/documents/${replaceDocCurrent.id}/reassign`, {
          document_type_id: null
        });
        console.log('Unassign response:', unassignRes.data);
      }
      
      // Then, assign the new document to the slot
      console.log('Assigning new doc:', selectedReplaceDoc.id, 'to type:', replaceDocTargetType);
      const assignRes = await api().put(`/documents/${selectedReplaceDoc.id}/reassign`, {
        document_type_id: replaceDocTargetType.id,
        document_type_name: replaceDocTargetType.name
      });
      console.log('Assign response:', assignRes.data);
      
      toast.success(`Document replaced successfully for ${replaceDocTargetType.name}`);
      setReplaceDocModalOpen(false);
      setSelectedReplaceDoc(null);
      setReplaceDocTargetType(null);
      setReplaceDocCurrent(null);
      
      // Clear preview cache immediately - revoke old blob URLs to free memory and force fresh fetch
      Object.values(previewUrls).forEach(preview => {
        if (preview?.url) {
          try { window.URL.revokeObjectURL(preview.url); } catch (e) {}
        }
      });
      setPreviewUrls({});
      setLoadingPreviews({});
      
      // Force refresh documents with a small delay to ensure DB update is complete
      setTimeout(() => {
        fetchDocuments();
      }, 500);
    } catch (error) {
      toast.error('Failed to replace document: ' + (error.response?.data?.detail || error.message));
      console.error('Replace document error:', error);
    } finally {
      setReplacingDocument(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try { await api().put(`/applications/${id}`, { ...application, desk_data: formData }); toast.success('Application saved'); } 
    catch (error) { toast.error('Failed to save application'); } 
    finally { setSaving(false); }
  };

  const handleShiftDesk = async () => {
    try {
      let targetDeskName;
      let deskHandler = null;

      if (isAgent) {
        // Agents don't pick — the admin-designated counselor handles it.
        if (!agentAssignedCounselor?.id) {
          toast.error('No counselor is attached to your account yet — please contact your admin.');
          return;
        }
        targetDeskName = 'Counselor';
        deskHandler = agentAssignedCounselor.id;
      } else {
        if (!shiftToRole) { toast.error('Please select a target desk'); return; }
        const selectedRole = roles.find(r => r.id === shiftToRole);
        targetDeskName = selectedRole?.name || 'Unknown';
        if (shiftToUser && shiftToUser !== 'none') deskHandler = shiftToUser;
      }

      const payload = { target_desk: targetDeskName, notes: shiftNotes };
      if (deskHandler) payload.desk_handler = deskHandler;

      await api().post(`/applications/${id}/shift-desk`, payload);
      const handlerName = deskHandler
        ? (isAgent ? agentAssignedCounselor.name : masterData.users.find(u => u.id === deskHandler)?.name)
        : null;
      toast.success(`Shifted to ${targetDeskName}${handlerName ? ` (Handler: ${handlerName})` : ''}`);
      setShiftDialogOpen(false); setTargetDesk(''); setShiftNotes(''); setShiftToUser(''); setShiftToRole('');
      // Refresh without changing the active tab
      fetchApplication(false);
      fetchActivityLogs();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to shift desk'); }
  };

  // Get roles that have permission for a specific desk
  const getRolesForDesk = (deskSlug) => {
    if (!deskSlug || !roles.length) return [];
    
    // Map desk slugs to expected permission patterns
    const deskPermissionMap = {
      'counselor': ['desk:counselor', 'counselor', 'applications:create'],
      'admission': ['desk:admission', 'admission'],
      'compliance': ['desk:compliance', 'compliance'],
      'visa': ['desk:visa', 'visa'],
      'document': ['desk:document', 'document', 'documents:'],
      'accounts': ['desk:accounts', 'accounts', 'finance']
    };
    
    const permissionPatterns = deskPermissionMap[deskSlug] || [deskSlug, `desk:${deskSlug}`];
    
    // Filter roles that have permission for this desk
    return roles.filter(role => {
      if (!role.permissions) return false;
      
      // Admin/wildcard permission - can access any desk
      if (role.permissions.includes('*')) return true;
      
      // Check if role has permission for this desk
      return role.permissions.some(perm => {
        const permLower = perm.toLowerCase();
        return permissionPatterns.some(pattern => permLower.includes(pattern.toLowerCase()));
      });
    });
  };

  // Get users who have a specific role
  const getUsersForRole = (roleId) => {
    if (!roleId) return [];
    const users = masterData?.users || [];
    
    const filteredUsers = users.filter(user => {
      return user.role_ids && Array.isArray(user.role_ids) && user.role_ids.includes(roleId);
    });
    
    console.log(`getUsersForRole(${roleId}): Found ${filteredUsers.length} users out of ${users.length} total`);
    return filteredUsers;
  };

  const handleStatusChange = async (status) => {
    try { await api().put(`/applications/${id}`, { status }); setApplication({ ...application, status }); toast.success('Status updated'); }
    catch (error) { toast.error('Failed to update status'); }
  };

  // Agent Chat Functions
  const fetchAgentChat = async () => {
    if (!application?.agent_id) return;
    try {
      const response = await api().get(`/applications/${id}/agent-chat`);
      setAgentChatMessages(response.data.messages || []);
      setAgentInfo(response.data.agent);
    } catch (error) {
      console.error('Failed to fetch agent chat:', error);
    }
  };

  const sendAgentChatMessage = async () => {
    if (!agentChatInput.trim()) return;
    setAgentChatSending(true);
    try {
      const response = await api().post(`/applications/${id}/agent-chat`, {
        message: agentChatInput.trim()
      });
      setAgentChatMessages(prev => [...prev, response.data]);
      setAgentChatInput('');
      setTimeout(() => agentChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setAgentChatSending(false);
    }
  };

  // Fetch agent chat when dialog opens
  useEffect(() => {
    if (agentChatOpen && application?.agent_id) {
      fetchAgentChat();
    }
  }, [agentChatOpen, application?.agent_id]);

  const handleFileUpload = async (e, documentTypeId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedExtensions = ['.pdf', '.zip', '.jpg', '.jpeg', '.png', '.rar', '.doc', '.docx'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(fileExt)) {
      toast.error('Invalid file type. Allowed: PDF, ZIP, JPG, JPEG, PNG, RAR, DOC, DOCX');
      e.target.value = '';
      return;
    }
    
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 10MB');
      e.target.value = '';
      return;
    }
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try { 
      await api().post(`/documents?application_id=${id}&document_type_id=${documentTypeId}`, formDataUpload, { headers: { 'Content-Type': 'multipart/form-data' } }); 
      toast.success('Document uploaded successfully'); 
      fetchDocuments(); 
    }
    catch (error) { 
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document'); 
    }
    
    // Reset file input
    e.target.value = '';
  };

  // Handle additional document upload
  const handleAdditionalDocUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!additionalDocName.trim()) {
      toast.error('Please enter a document name first');
      e.target.value = '';
      return;
    }
    
    // Validate file type
    const allowedExtensions = ['.pdf', '.zip', '.jpg', '.jpeg', '.png', '.rar', '.doc', '.docx'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(fileExt)) {
      toast.error('Invalid file type. Allowed: PDF, ZIP, JPG, JPEG, PNG, RAR, DOC, DOCX');
      e.target.value = '';
      return;
    }
    
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 10MB');
      e.target.value = '';
      return;
    }
    
    setAdditionalDocUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try { 
      await api().post(
        `/documents/additional?application_id=${id}&document_name=${encodeURIComponent(additionalDocName.trim())}`, 
        formDataUpload, 
        { headers: { 'Content-Type': 'multipart/form-data' } }
      ); 
      toast.success('Additional document uploaded successfully'); 
      fetchDocuments();
      setAdditionalDocName('');
    }
    catch (error) { 
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document'); 
    } finally {
      setAdditionalDocUploading(false);
      e.target.value = '';
    }
  };

  // Handle ZIP/7Z file upload
  const handleZipUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const filename = file.name.toLowerCase();
    if (!filename.endsWith('.zip') && !filename.endsWith('.7z')) {
      toast.error('Please select a ZIP or 7Z file');
      return;
    }
    
    setZipUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api().post(
        `/documents/zip-upload?application_id=${id}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const { total_files, auto_matched, additional, message, errors } = response.data;
      
      toast.success(message);
      
      if (errors && errors.length > 0) {
        errors.forEach(err => toast.warning(err));
      }
      
      fetchDocuments();
    } catch (error) {
      console.error('Archive upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload archive file');
    } finally {
      setZipUploading(false);
      e.target.value = '';
    }
  };

  // Handle document reassignment to a different type
  const handleDocumentReassign = async (docId, newDocTypeId) => {
    try {
      const response = await api().put(`/documents/${docId}/reassign`, {
        document_type_id: newDocTypeId
      });
      
      toast.success(response.data.message || 'Document reassigned successfully');
      setReassigningDocId(null);
      fetchDocuments();
    } catch (error) {
      console.error('Reassignment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reassign document');
    }
  };

  const updateDeskField = (desk, field, value) => {
    setFormData(prev => ({ ...prev, [desk]: { ...prev[desk], [field]: value } }));
  };

  const getDeskColor = (desk) => {
    const colors = { counselor: 'bg-blue-500/10 text-blue-500', admission: 'bg-amber-500/10 text-amber-500', status: 'bg-gray-500/10 text-gray-500', compliance: 'bg-yellow-500/10 text-yellow-500', visa: 'bg-green-500/10 text-green-500', document: 'bg-orange-500/10 text-orange-500' };
    return colors[desk?.toLowerCase()] || 'bg-primary/10 text-primary';
  };

  const getDocumentStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'verified': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'uploaded': return <Clock className="w-4 h-4 text-yellow-500" />;
      default: return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const hasTabAccess = (tabSlug) => true;

  if (loading) { return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>; }

  return (
    <div className="space-y-6" data-testid="application-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={goBack}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{application?.student_name}</h1>
            <p className="text-muted-foreground">{application?.student_email} • App ID: <span className="font-mono">{application?.application_code || (application?.id ? String(application.id).slice(0, 8).toUpperCase() : 'N/A')}</span></p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Previous/Next Navigation */}
          <div className="flex items-center gap-1 border rounded-lg px-2 py-1 bg-muted/30">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(`/applications/${neighbors.previous_id}?tab=${returnTab}&page=${returnPage}${returnLimit !== '20' ? `&limit=${returnLimit}` : ''}`)}
              disabled={!neighbors.previous_id}
              className="h-8 px-2"
              data-testid="prev-application-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground px-2 border-x">
              {neighbors.current_index} / {neighbors.total}
            </span>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(`/applications/${neighbors.next_id}?tab=${returnTab}&page=${returnPage}${returnLimit !== '20' ? `&limit=${returnLimit}` : ''}`)}
              disabled={!neighbors.next_id}
              className="h-8 px-2"
              data-testid="next-application-btn"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <Dialog open={shiftDialogOpen} onOpenChange={(open) => { setShiftDialogOpen(open); if (!open) { setTargetDesk(''); setShiftNotes(''); } }}>
            <DialogTrigger asChild><Button variant="outline" data-testid="shift-desk-btn"><ArrowRightLeft className="w-4 h-4 mr-2" />Shift Desk</Button></DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Shift to Another Desk</DialogTitle><DialogDescription>Move this application to a different desk for processing</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Current Status</Label>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Current Desk:</span>
                      <Badge className={getDeskColor(application?.current_desk)}>{application?.current_desk}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Case Owner:</span>
                      <span className="text-sm font-medium">{application?.assigned_to_name || 'Unassigned'}</span>
                      <span className="text-xs text-muted-foreground">(permanent)</span>
                    </div>
                  </div>
                </div>
                
                {isAgent ? (
                  /* Agents don't pick a desk manually — the app auto-routes
                     to the counselor the admin attached to this agent. */
                  <div className="space-y-2">
                    <Label>Shift to</Label>
                    {agentAssignedCounselor ? (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 p-3" data-testid="agent-shift-target">
                        <div className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-300 font-semibold">Assigned Counselor</div>
                        <div className="text-sm font-medium text-emerald-900 dark:text-emerald-100 mt-0.5">{agentAssignedCounselor.name}</div>
                        {agentAssignedCounselor.email && <div className="text-xs text-emerald-700/80 dark:text-emerald-300/80">{agentAssignedCounselor.email}</div>}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-sm" data-testid="agent-shift-no-counselor">
                        <span className="font-medium text-amber-800 dark:text-amber-200">No counselor is attached to your account yet.</span>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">Please contact your admin — once a counselor is assigned to you, shifting the application will route it to them automatically.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Target Desk <span className="text-destructive">*</span></Label>
                    <Select value={shiftToRole || 'none'} onValueChange={(v) => { setShiftToRole(v === 'none' ? '' : v); setShiftToUser(''); }}>
                      <SelectTrigger data-testid="shift-role-select"><SelectValue placeholder="Select target desk" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select target desk</SelectItem>
                        {roles.map(role => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {shiftToRole && !isAgent && (
                  <div className="space-y-2">
                    <Label>Desk Handler (Optional)</Label>
                    <p className="text-xs text-muted-foreground mb-1">
                      Select who will handle this case at the target desk. This does NOT change the Case Owner.
                    </p>
                    {getUsersForRole(shiftToRole).length > 0 ? (
                      <Select value={shiftToUser || 'none'} onValueChange={(v) => setShiftToUser(v === 'none' ? '' : v)}>
                        <SelectTrigger data-testid="shift-user-select"><SelectValue placeholder="Select desk handler" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- No specific handler --</SelectItem>
                          {getUsersForRole(shiftToRole).map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} {u.email && <span className="text-muted-foreground ml-1">({u.email})</span>}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-xs">
                        No team members found with "{roles.find(r => r.id === shiftToRole)?.name}" role.
                      </div>
                    )}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea value={shiftNotes} onChange={(e) => setShiftNotes(e.target.value)} placeholder="Add any notes for this desk shift..." data-testid="shift-notes-input" />
                </div>
                
                <Button
                  onClick={handleShiftDesk}
                  className="w-full"
                  disabled={isAgent ? !agentAssignedCounselor : !shiftToRole}
                  data-testid="confirm-shift-btn"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  {isAgent ? 'Shift to my Counselor' : 'Shift Application'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button onClick={handleSave} disabled={saving} data-testid="save-application-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save Changes</Button>
        </div>
      </div>

      {/* Status Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Current Desk:</span><Badge className={getDeskColor(application?.current_desk)}>{application?.current_desk}</Badge></div>
            {!isAgent && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <Select value={application?.status || 'draft'} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-[140px]" data-testid="app-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {masterData.applicationStatuses?.length > 0 ? (
                    masterData.applicationStatuses.map(s => (
                      <SelectItem key={s.id} value={s.name.toLowerCase().replace(/\s+/g, '_')}>{s.name}</SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            )}
            {application?.is_agent_submission && <Badge variant="outline">Agent Submission</Badge>}
            {/* Chat Button with Selection */}
            {application?.agent_id && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    data-testid="chat-menu-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    Chat
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
                    Who would you like to message?
                  </div>
                  <DropdownMenuItem 
                    onClick={() => setAgentChatOpen(true)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-3 py-1">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium">Agent</span>
                        <span className="text-xs text-muted-foreground">
                          {application?.transferred_by_agent_name || 'Submitting Agent'}
                        </span>
                      </div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => {
                      // Open team chat widget - we'll dispatch a custom event
                      window.dispatchEvent(new CustomEvent('openTeamChat', { 
                        detail: { applicationId: id, applicationName: application?.student_name } 
                      }));
                    }}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-3 py-1">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                        <Users className="w-4 h-4 text-amber-500" />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium">Team Members</span>
                        <span className="text-xs text-muted-foreground">Internal team discussion</span>
                      </div>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {!application?.agent_id && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('openTeamChat', { 
                    detail: { applicationId: id, applicationName: application?.student_name } 
                  }));
                }}
                className="text-amber-500 border-amber-200 hover:bg-amber-50"
                data-testid="team-chat-btn"
              >
                <MessageCircle className="w-4 h-4 mr-1" />
                Team Chat
              </Button>
            )}
            {!isAgent && (application?.document_upload_token ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Student Upload Link:</span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const uploadUrl = `${window.location.origin}/student/documents/${application.document_upload_token}`;
                    navigator.clipboard.writeText(uploadUrl);
                    toast.success('Upload link copied to clipboard!');
                  }}
                  data-testid="copy-upload-link-btn"
                >
                  <Link2 className="w-4 h-4 mr-1" />
                  Copy Link
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={sendWelcomeEmails}
                  disabled={sendingEmails}
                  data-testid="send-emails-btn"
                >
                  {sendingEmails ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                  Send Emails
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={sendWelcomeEmails}
                  disabled={sendingEmails}
                  data-testid="generate-link-btn"
                >
                  {sendingEmails ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                  Generate Upload Link & Send Emails
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-2 ml-auto"><span className="text-sm text-muted-foreground">Case Owner:</span><span className="text-sm font-medium">{application?.assigned_to_name || 'Unassigned'}</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          {renderDeskTabTriggers()}
        </TabsList>

        <TabsContent value="counselor"><CounselorTab formData={formData} updateDeskField={updateDeskField} masterData={masterData} fetchMasterData={fetchMasterData} readOnly={!canEditDesk('counselor')} api={api} documents={documents} applicationId={id} onDocumentUploaded={fetchDocuments} documentTypes={documentTypes} currentUser={user} isAgent={isAgent} /></TabsContent>
        <TabsContent value="admission"><AdmissionTab formData={formData} updateDeskField={updateDeskField} masterData={masterData} fetchMasterData={fetchMasterData} readOnly={!canEditDesk('admission')} api={api} isAgent={isAgent} /></TabsContent>
        
        <TabsContent value="status">
          <Card>
            <CardHeader><CardTitle className="text-primary">Application Status</CardTitle><CardDescription>Track the overall application status {!canEditDesk('status') && <Badge variant="outline" className="ml-2">View Only</Badge>}</CardDescription></CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lead Status</Label>
                  <Select value={formData.status?.lead_status || 'none'} onValueChange={(v) => updateDeskField('status', 'lead_status', v === 'none' ? '' : v)} disabled={!canEditDesk('status')}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select</SelectItem>
                      {masterData.leadStatuses?.map(s => (
                        <SelectItem key={s.id} value={s.name.toLowerCase().replace(/\s+/g, '_')}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Application Status</Label>
                  <Select value={formData.status?.application_status || 'none'} onValueChange={(v) => updateDeskField('status', 'application_status', v === 'none' ? '' : v)} disabled={!canEditDesk('status')}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select</SelectItem>
                      {masterData.applicationStatuses?.map(s => (
                        <SelectItem key={s.id} value={s.name.toLowerCase().replace(/\s+/g, '_')}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Offer Status</Label>
                  <Select value={formData.status?.offer_status || 'none'} onValueChange={(v) => updateDeskField('status', 'offer_status', v === 'none' ? '' : v)} disabled={!canEditDesk('status')}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select</SelectItem>
                      {masterData.offerStatuses?.map(s => (
                        <SelectItem key={s.id} value={s.name.toLowerCase().replace(/\s+/g, '_')}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Remarks</Label><Textarea value={formData.status?.remarks || ''} onChange={(e) => updateDeskField('status', 'remarks', e.target.value)} rows={4} readOnly={!canEditDesk('status')} /></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance"><ComplianceTab formData={formData} updateDeskField={updateDeskField} masterData={masterData} readOnly={!canEditDesk('compliance')} /></TabsContent>
        <TabsContent value="visa"><VisaTab formData={formData} updateDeskField={updateDeskField} masterData={masterData} readOnly={!canEditDesk('visa')} /></TabsContent>

        <TabsContent value="document">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-primary">Documents</CardTitle>
                  <CardDescription>
                    Required documents and uploads {!canEditDesk('document') && <Badge variant="outline" className="ml-2">View Only</Badge>}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  {/* Download All as ZIP */}
                  <Button
                    variant="outline"
                    onClick={downloadAllDocumentsAsZip}
                    disabled={zipDownloading || documents.length === 0}
                    className="gap-2"
                    data-testid="documents-download-zip-btn"
                    title={documents.length === 0 ? 'Upload at least one document first' : `Download all ${documents.length} documents in a single ZIP`}
                  >
                    {zipDownloading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Bundling...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Download All ({documents.length})
                      </>
                    )}
                  </Button>
                  {/* ZIP/7Z Upload Button */}
                  <div>
                    <input
                      type="file"
                      ref={zipInputRef}
                      onChange={handleZipUpload}
                      accept=".zip,.7z"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => zipInputRef.current?.click()}
                      disabled={zipUploading}
                      className="gap-2"
                    >
                      {zipUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Extracting...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Import ZIP/7Z
                        </>
                      )}
                    </Button>
                  </div>
                  {documentTypesMetadata.hasRequiredDocs && (
                    <div className="text-right">
                      <Badge variant="outline" className="text-sm">
                        {documents.filter(d => documentTypes.some(dt => 
                          d.document_type_id === dt.id || 
                          (d.document_type_name && d.document_type_name.toLowerCase() === dt.name.toLowerCase()) ||
                          (d.file_name && dt.name.toLowerCase().split(' ').some(word => d.file_name.toLowerCase().includes(word)))
                        )).length} / {documentTypesMetadata.totalRequired} Uploaded
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {documentTypesMetadata.countryName} • {documentTypesMetadata.degreeTypeName}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Required Documents Section */}
                {documentTypes.length > 0 ? (
                  <>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-px flex-1 bg-border"></div>
                      <span className="text-sm font-medium text-muted-foreground px-2">Required Documents</span>
                      <div className="h-px flex-1 bg-border"></div>
                    </div>
                    {documentTypes.map((docType) => {
                      // Check for document by document_type_id OR by document_type_name (case-insensitive)
                      // Priority: exact ID match, then name match
                      const uploadedDoc = documents.find(d => 
                        d.document_type_id === docType.id || 
                        (d.document_type_name && d.document_type_name.toLowerCase() === docType.name.toLowerCase())
                      );
                      const isFromAI = uploadedDoc && uploadedDoc.source === 'ai_profiler';
                      const isFromStudent = uploadedDoc && uploadedDoc.source === 'student_upload';
                      const isFromScan = uploadedDoc && uploadedDoc.source === 'counselor_scan';
                      return (
                        <div key={docType.id} className={`flex items-center justify-between p-4 rounded-lg border ${
                          uploadedDoc 
                            ? isFromAI ? 'border-primary/30 bg-primary/5' 
                              : isFromStudent ? 'border-cyan-200 bg-cyan-50' 
                              : isFromScan ? 'border-amber-200 bg-amber-50'
                              : 'border-green-200 bg-green-50'
                            : 'border-amber-200 bg-amber-50/50'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              uploadedDoc ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'
                            }`}>
                              {uploadedDoc ? <CheckCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                            </div>
                            <div>
                              <p className="font-medium flex items-center gap-2">
                                {docType.name}
                                {docType.isRequired && <Badge variant="destructive" className="text-xs">Required</Badge>}
                                {isFromAI && <Badge variant="secondary" className="text-xs">AI</Badge>}
                                {isFromStudent && <Badge className="text-xs bg-cyan-600">Student</Badge>}
                                {isFromScan && <Badge className="text-xs bg-amber-500">Scanned</Badge>}
                              </p>
                              {uploadedDoc ? (
                                <p className="text-sm text-muted-foreground">{uploadedDoc.file_name || uploadedDoc.filename}</p>
                              ) : (
                                <p className="text-sm text-amber-600">Not uploaded yet</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {uploadedDoc ? (
                              <>
                                <DocumentPreviewButton 
                                  documentId={uploadedDoc.id} 
                                  filename={uploadedDoc.file_name || uploadedDoc.filename}
                                  api={api}
                                  onViewClick={() => handleDocumentView(uploadedDoc.id, uploadedDoc.file_name || uploadedDoc.filename)}
                                />
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => openReplaceDocModal(docType, uploadedDoc)} 
                                  title="Replace Document"
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleDocumentDownload(uploadedDoc.id, uploadedDoc.file_name || uploadedDoc.filename)} title="Download"><Download className="w-4 h-4" /></Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => handleRequiredDocUnlink(uploadedDoc.id)}
                                  title="Remove from required slot (file stays in AI Documents)"
                                  data-testid={`unlink-required-doc-${uploadedDoc.id}`}
                                ><Trash2 className="w-4 h-4" /></Button>
                                {getDocumentStatusIcon(uploadedDoc.status)}<Badge variant="outline">{uploadedDoc.status}</Badge>
                              </>
                            ) : (
                              <>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="default" size="sm" className="bg-primary">
                                      <Upload className="w-4 h-4 mr-2" />Upload
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem 
                                      className="cursor-pointer"
                                      onSelect={(e) => {
                                        e.preventDefault();
                                        document.getElementById(`file-upload-${docType.id}`)?.click();
                                      }}
                                    >
                                      <Upload className="w-4 h-4 mr-2" />
                                      Upload from Computer
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openDocumentGallery(docType)}>
                                      <FileText className="w-4 h-4 mr-2" />
                                      Select from Gallery
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <input 
                                  type="file" 
                                  id={`file-upload-${docType.id}`}
                                  className="hidden" 
                                  accept=".pdf,.zip,.jpg,.jpeg,.png,.rar,.doc,.docx" 
                                  onChange={(e) => handleFileUpload(e, docType.id)} 
                                  data-testid={`upload-${docType.id}`} 
                                />
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed">
                    <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="font-medium text-muted-foreground">No required documents configured</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {!application?.desk_data?.counselor?.nationality && !application?.desk_data?.admission?.degree_type ? (
                        'Set nationality and degree type to see required documents'
                      ) : !application?.desk_data?.counselor?.nationality ? (
                        'Set nationality in Counselor tab to see required documents'
                      ) : !application?.desk_data?.admission?.degree_type ? (
                        'Set degree type in Admission tab to see required documents'
                      ) : (
                        'Configure required documents in Core Settings → Required Docs'
                      )}
                    </p>
                  </div>
                )}
                
                {/* Show web form uploaded documents that don't match any document type */}
                {documents.filter(d => d.source === 'web_form' && !documentTypes.find(dt => dt.id === d.document_type_id)).length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground mb-3">Web Form Uploads</p>
                    {documents.filter(d => d.source === 'web_form' && !documentTypes.find(dt => dt.id === d.document_type_id)).map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg border border-border mb-2">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{doc.file_name || doc.filename || 'Uploaded Document'}</p>
                            <p className="text-sm text-muted-foreground">{doc.file_type} • From Web Form</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <DocumentPreviewButton 
                            documentId={doc.id} 
                            filename={doc.file_name || doc.filename}
                            api={api}
                            onViewClick={() => handleDocumentView(doc.id, doc.file_name || doc.filename)}
                          />
                          <Button variant="outline" size="sm" onClick={() => handleDocumentDownload(doc.id, doc.file_name || doc.filename)} title="Download">
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDocumentDelete(doc.id)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          {getDocumentStatusIcon(doc.status)}
                          <Badge variant="outline">{doc.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* r36 — WhatsApp-funnel uploaded documents (no document_type_id) */}
                {documents.filter(d => d.source === 'whatsapp_funnel' && !documentTypes.find(dt => dt.id === d.document_type_id)).length > 0 && (
                  <div className="mt-6 pt-4 border-t" data-testid="whatsapp-uploads-section">
                    <p className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700 text-[11px] font-bold">W</span>
                      WhatsApp Uploads
                    </p>
                    {documents.filter(d => d.source === 'whatsapp_funnel' && !documentTypes.find(dt => dt.id === d.document_type_id)).map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg border border-green-200 bg-green-50/50 mb-2" data-testid={`whatsapp-doc-${doc.id}`}>
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-green-700" />
                          <div>
                            <p className="font-medium">{doc.file_name || doc.filename || 'WhatsApp Document'}</p>
                            <p className="text-sm text-muted-foreground">{doc.file_type} • via WhatsApp Funnel</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <DocumentPreviewButton
                            documentId={doc.id}
                            filename={doc.file_name || doc.filename}
                            api={api}
                            onViewClick={() => handleDocumentView(doc.id, doc.file_name || doc.filename)}
                          />
                          <Button variant="outline" size="sm" onClick={() => handleDocumentDownload(doc.id, doc.file_name || doc.filename)} title="Download">
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDocumentDelete(doc.id)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Show AI Profiler uploaded documents that don't match any document type slot */}
                {documents.filter(d => d.source === 'ai_profiler' && !documentTypes.find(dt => dt.id === d.document_type_id)).length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground mb-3">Other AI Counselor Uploads</p>
                    {documents.filter(d => d.source === 'ai_profiler' && !documentTypes.find(dt => dt.id === d.document_type_id)).map((doc) => {
                      const filename = doc.file_name || doc.filename || '';
                      const isPdf = filename.toLowerCase().endsWith('.pdf');
                      return (
                        <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg border border-primary/20 bg-primary/5 mb-2">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-primary" />
                            <div>
                              <p className="font-medium">{filename || 'Uploaded Document'}</p>
                              <p className="text-sm text-muted-foreground">
                                {doc.document_type || doc.file_type} • From AI Counselor
                                {doc.page_number && <span className="ml-2 text-xs text-primary">(Page {doc.page_number} of {doc.total_pages})</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <DocumentPreviewButton 
                              documentId={doc.id} 
                              filename={filename}
                              api={api}
                              onViewClick={() => handleDocumentView(doc.id, filename)}
                            />
                            {isPdf && !doc.split_from && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleSplitPdf(doc.id, filename)} 
                                title="Split PDF into pages"
                                disabled={splittingPdfId === doc.id}
                              >
                                {splittingPdfId === doc.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Scissors className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => handleDocumentDownload(doc.id, filename)} title="Download">
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDocumentDelete(doc.id)} title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <Badge variant="default">AI Attached</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Show Student uploaded documents that don't match any document type slot */}
                {documents.filter(d => d.source === 'student_upload' && !documentTypes.find(dt => dt.id === d.document_type_id || (d.document_type_name && d.document_type_name.toLowerCase() === dt.name.toLowerCase()))).length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground mb-3">📤 Other Student Uploaded Documents</p>
                    {documents.filter(d => d.source === 'student_upload' && !documentTypes.find(dt => dt.id === d.document_type_id || (d.document_type_name && d.document_type_name.toLowerCase() === dt.name.toLowerCase()))).map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg border border-cyan-200 bg-cyan-50 mb-2">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-cyan-600" />
                          <div>
                            <p className="font-medium">{doc.document_type_name || doc.file_name || doc.filename || 'Uploaded Document'}</p>
                            <p className="text-sm text-muted-foreground">{doc.file_name || doc.filename} • Uploaded by Student</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <DocumentPreviewButton 
                            documentId={doc.id} 
                            filename={doc.file_name || doc.filename}
                            api={api}
                            onViewClick={() => handleDocumentView(doc.id, doc.file_name || doc.filename)}
                          />
                          <Button variant="outline" size="sm" onClick={() => handleDocumentDownload(doc.id, doc.file_name || doc.filename)} title="Download">
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDocumentDelete(doc.id)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Badge className="bg-cyan-600">Student Upload</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Scanned Documents Section */}
                {documents.filter(d => d.source === 'counselor_scan').length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground mb-3">📋 Scanned Documents (from Counselor Tab)</p>
                    {documents.filter(d => d.source === 'counselor_scan').map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50 mb-2">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-amber-500" />
                          <div>
                            <p className="font-medium">{doc.document_type_name || doc.file_name || doc.filename || 'Scanned Document'}</p>
                            <p className="text-sm text-muted-foreground">{doc.file_name || doc.filename} • Scanned</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <DocumentPreviewButton 
                            documentId={doc.id} 
                            filename={doc.file_name || doc.filename}
                            api={api}
                            onViewClick={() => handleDocumentView(doc.id, doc.file_name || doc.filename)}
                          />
                          <Button variant="outline" size="sm" onClick={() => handleDocumentDownload(doc.id, doc.file_name || doc.filename)} title="Download">
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDocumentDelete(doc.id)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Badge className="bg-amber-500">Scanned</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* ZIP Imported Documents that were auto-matched */}
                {documents.filter(d => d.source === 'zip_upload' && d.document_type_id && d.auto_matched).length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground mb-3">📦 ZIP Imported (Auto-Matched)</p>
                    {documents.filter(d => d.source === 'zip_upload' && d.document_type_id && d.auto_matched).map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50 mb-2">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-amber-500" />
                          <div>
                            <p className="font-medium">{doc.document_type_name || doc.file_name || 'ZIP Document'}</p>
                            <p className="text-sm text-muted-foreground">
                              {doc.file_name || doc.filename} • Matched to "{doc.document_type_name}"
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Reassignment Dropdown */}
                          {documentTypes.length > 0 && (
                            <Select
                              value=""
                              onValueChange={(value) => handleDocumentReassign(doc.id, value)}
                            >
                              <SelectTrigger className="w-[160px] h-8 text-xs">
                                <SelectValue placeholder="Reassign to..." />
                              </SelectTrigger>
                              <SelectContent>
                                {documentTypes.map(dt => (
                                  <SelectItem key={dt.id} value={dt.id} className="text-xs">
                                    {dt.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <DocumentPreviewButton 
                            documentId={doc.id} 
                            filename={doc.file_name || doc.filename}
                            api={api}
                            onViewClick={() => handleDocumentView(doc.id, doc.file_name || doc.filename)}
                          />
                          <Button variant="outline" size="sm" onClick={() => handleDocumentDownload(doc.id, doc.file_name || doc.filename)} title="Download">
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDocumentDelete(doc.id)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Badge className="bg-amber-500">ZIP Auto</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Unidentified / Additional Documents Section */}
                <div className="mt-6 pt-4 border-t">
                  <p className="text-sm font-medium text-muted-foreground mb-3">📎 Unidentified / Additional Documents</p>
                  <p className="text-xs text-muted-foreground mb-3">Documents from Web Forms, AI Counselor, or ZIP imports that need to be assigned to a required document type.</p>
                  
                  {/* Upload Additional Document */}
                  <div className="p-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 mb-4">
                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                      <div className="flex-1 w-full">
                        <Label htmlFor="additionalDocName" className="text-sm">Document Name</Label>
                        <Input
                          id="additionalDocName"
                          placeholder="e.g., Bank Statement, Medical Certificate"
                          value={additionalDocName}
                          onChange={(e) => setAdditionalDocName(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <input
                          type="file"
                          ref={additionalDocInputRef}
                          onChange={handleAdditionalDocUpload}
                          accept=".pdf,.zip,.jpg,.jpeg,.png,.rar,.doc,.docx"
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          onClick={() => additionalDocInputRef.current?.click()}
                          disabled={!additionalDocName.trim() || additionalDocUploading}
                        >
                          {additionalDocUploading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Browse & Upload
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Enter a name for the document, then click to browse and upload.</p>
                  </div>
                  
                  {/* List Additional/Unidentified Documents - from web forms, AI Counselor, ZIP uploads, Agent Portal without matched type */}
                  {documents.filter(d => 
                    d.source === 'additional' || 
                    d.source === 'agent_portal' ||
                    (d.source === 'zip_upload' && !d.document_type_id) ||
                    (d.source === 'web_form' && !documentTypes.some(dt => dt.name?.toLowerCase() === d.document_type_name?.toLowerCase())) ||
                    (d.source === 'ai_counselor' && !documentTypes.some(dt => dt.name?.toLowerCase() === d.document_type_name?.toLowerCase()))
                  ).length > 0 ? (
                    documents.filter(d => 
                      d.source === 'additional' || 
                      d.source === 'agent_portal' ||
                      (d.source === 'zip_upload' && !d.document_type_id) ||
                      (d.source === 'web_form' && !documentTypes.some(dt => dt.name?.toLowerCase() === d.document_type_name?.toLowerCase())) ||
                      (d.source === 'ai_counselor' && !documentTypes.some(dt => dt.name?.toLowerCase() === d.document_type_name?.toLowerCase()))
                    ).map((doc) => (
                      <div key={doc.id} className={`flex items-center justify-between p-4 rounded-lg border mb-2 ${
                        doc.from_zip ? 'border-amber-200 bg-amber-50' : 
                        doc.source === 'web_form' ? 'border-blue-200 bg-blue-50' :
                        doc.source === 'ai_counselor' ? 'border-green-200 bg-green-50' :
                        doc.source === 'agent_portal' ? 'border-orange-200 bg-orange-50' :
                        'border-gray-200 bg-white'
                      }`}>
                        <div className="flex items-center gap-3">
                          <FileText className={`w-5 h-5 ${
                            doc.from_zip ? 'text-amber-500' : 
                            doc.source === 'web_form' ? 'text-blue-600' :
                            doc.source === 'ai_counselor' ? 'text-green-600' :
                            doc.source === 'agent_portal' ? 'text-orange-600' :
                            'text-gray-600'
                          }`} />
                          <div>
                            <p className="font-medium">{doc.document_type_name || doc.file_name || 'Additional Document'}</p>
                            <p className="text-sm text-muted-foreground">
                              {doc.file_name || doc.filename}
                              {doc.from_zip && <span className="ml-2 text-amber-500">• From ZIP</span>}
                              {doc.source === 'agent_portal' && <span className="ml-2 text-orange-600">• Agent Upload</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Reassignment Dropdown - Shows required docs for this applicant's country/degree */}
                          {documentTypes.length > 0 && (
                            <Select
                              value=""
                              onValueChange={(value) => handleDocumentReassign(doc.id, value)}
                            >
                              <SelectTrigger className="w-[200px] h-8 text-xs border-orange-300 bg-orange-50">
                                <SelectValue placeholder="Assign to required doc..." />
                              </SelectTrigger>
                              <SelectContent>
                                <div className="px-2 py-1 text-xs text-muted-foreground border-b mb-1">
                                  Required Documents
                                </div>
                                {documentTypes.map(dt => (
                                  <SelectItem key={dt.id} value={dt.id} className="text-xs">
                                    {dt.isRequired && <span className="text-orange-500 mr-1">●</span>}
                                    {dt.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <DocumentPreviewButton 
                            documentId={doc.id} 
                            filename={doc.file_name || doc.filename}
                            api={api}
                            onViewClick={() => handleDocumentView(doc.id, doc.file_name || doc.filename)}
                          />
                          {(doc.file_name || doc.filename || '').toLowerCase().endsWith('.pdf') && !doc.split_from && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleSplitPdf(doc.id, doc.file_name || doc.filename)} 
                              title="Split PDF into pages"
                              disabled={splittingPdfId === doc.id}
                            >
                              {splittingPdfId === doc.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Scissors className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => handleDocumentDownload(doc.id, doc.file_name || doc.filename)} title="Download">
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDocumentDelete(doc.id)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Badge 
                            variant="default" 
                            className={
                              doc.from_zip ? "bg-amber-500" : 
                              doc.source === 'web_form' ? "bg-blue-600" :
                              doc.source === 'ai_counselor' ? "bg-green-600" :
                              doc.source === 'agent_portal' ? "bg-orange-600" :
                              "bg-gray-500"
                            }
                          >
                            {doc.from_zip ? "ZIP Import" : 
                             doc.source === 'web_form' ? "Web Form" :
                             doc.source === 'ai_counselor' ? "AI Counselor" :
                             doc.source === 'agent_portal' ? "Agent Upload" :
                             "Unidentified"}
                          </Badge>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No unidentified documents</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Activity Log */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
          <CardDescription>Complete history of all activities for this application</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px]">
            {activityLogs.length > 0 ? (
              <div className="space-y-3">
                {activityLogs.map((log, index) => {
                  // Determine icon and color based on action type
                  let iconColor = 'bg-primary';
                  let actionText = log.action;
                  let detailsText = '';
                  
                  // Format action text and determine color
                  if (log.action === 'email_sent' || log.action === 'reminder_email_sent') {
                    iconColor = 'bg-blue-500';
                    actionText = '📧 Email Sent';
                    detailsText = log.details?.template_type ? `Template: ${log.details.template_type}` : '';
                  } else if (log.action === 'sms_sent') {
                    iconColor = 'bg-green-500';
                    actionText = '📱 SMS Sent';
                    detailsText = log.details?.message_preview || '';
                  } else if (log.action === 'reminder_sent') {
                    iconColor = 'bg-orange-500';
                    actionText = '⏰ Reminder Sent';
                    detailsText = log.details?.document_name ? `For: ${log.details.document_name}` : '';
                  } else if (log.action === 'desk_shifted' || log.action === 'application_shifted') {
                    iconColor = 'bg-amber-500';
                    actionText = '🔄 Desk Shifted';
                    detailsText = log.details?.from_desk ? `${log.details.from_desk} → ${log.details.to_desk}` : '';
                  } else if (log.action === 'document_uploaded') {
                    iconColor = 'bg-emerald-500';
                    actionText = '📎 Document Uploaded';
                    detailsText = log.details?.filename || log.details?.document_type_name || '';
                  } else if (log.action === 'document_deleted') {
                    iconColor = 'bg-red-500';
                    actionText = '🗑️ Document Deleted';
                    detailsText = log.details?.filename || '';
                  } else if (log.action === 'document_reassigned') {
                    iconColor = 'bg-amber-500';
                    actionText = '📋 Document Reassigned';
                    detailsText = log.details?.from_type ? `${log.details.from_type} → ${log.details.to_type}` : '';
                  } else if (log.action === 'status_changed') {
                    iconColor = 'bg-amber-500';
                    actionText = '📊 Status Changed';
                    detailsText = log.details?.from_status ? `${log.details.from_status} → ${log.details.to_status}` : '';
                  } else if (log.action === 'application_created') {
                    iconColor = 'bg-green-600';
                    actionText = '➕ Application Created';
                    detailsText = log.details?.source || '';
                  } else if (log.action === 'application_assigned') {
                    iconColor = 'bg-blue-600';
                    actionText = '👤 Assigned';
                    detailsText = log.details?.assigned_to || '';
                  } else if (log.action === 'chat_message_deleted') {
                    iconColor = 'bg-gray-500';
                    actionText = '💬 Chat Message Deleted';
                  } else if (log.action === 'note_added') {
                    iconColor = 'bg-yellow-500';
                    actionText = '📝 Note Added';
                  } else if (log.action === 'application_archived') {
                    iconColor = 'bg-gray-600';
                    actionText = '📦 Archived';
                    detailsText = log.details?.reason || '';
                  } else if (log.action === 'application_unarchived') {
                    iconColor = 'bg-green-500';
                    actionText = '📤 Unarchived';
                  }
                  
                  return (
                    <div key={index} className="flex items-start gap-3 pb-3 border-b border-border last:border-0">
                      <div className={`w-2 h-2 mt-2 rounded-full ${iconColor}`} />
                      <div className="flex-1">
                        <p className="text-sm">
                          <span className="font-medium">{actionText}</span>
                          {detailsText && <span className="text-muted-foreground ml-2">{detailsText}</span>}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString()}
                          {log.user_name && <span className="ml-2">by {log.user_name}</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-center text-muted-foreground py-8">No activity yet</p>}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Agent Chat Dialog */}
      <Dialog open={agentChatOpen} onOpenChange={setAgentChatOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-500" />
              Chat with Agent
            </DialogTitle>
            <DialogDescription>
              {agentInfo ? (
                <span className="flex items-center gap-2 mt-1">
                  <User className="w-4 h-4" />
                  Agent: <strong>{agentInfo.name}</strong>
                </span>
              ) : 'Communication with the agent who submitted this application'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col h-[400px]">
            <ScrollArea className="flex-1 p-4 border rounded-lg bg-muted/20">
              {agentChatMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <MessageCircle className="w-12 h-12 mb-2 opacity-30" />
                  <p className="text-sm">No messages yet</p>
                  <p className="text-xs">Start the conversation with the agent!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {agentChatMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex flex-col ${msg.sender_type === 'org_team' ? 'items-end' : 'items-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-2 ${
                          msg.sender_type === 'org_team'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-blue-100 text-blue-900'
                        }`}
                      >
                        <p className="text-sm">{msg.message}</p>
                      </div>
                      <span className="text-xs text-muted-foreground mt-1">
                        {msg.sender_name} • {new Date(msg.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                  <div ref={agentChatEndRef} />
                </div>
              )}
            </ScrollArea>
            <div className="flex gap-2 mt-3">
              <Input
                value={agentChatInput}
                onChange={(e) => setAgentChatInput(e.target.value)}
                placeholder="Type your message to the agent..."
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendAgentChatMessage()}
              />
              <Button onClick={sendAgentChatMessage} disabled={agentChatSending || !agentChatInput.trim()}>
                {agentChatSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Gallery Dialog */}
      <Dialog open={docGalleryOpen} onOpenChange={setDocGalleryOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Document Gallery
            </DialogTitle>
            <DialogDescription>
              Select a document to attach as <strong>{docGalleryTargetType?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[400px] pr-4">
            {documents.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-12">
                <FileText className="w-16 h-16 mb-4 opacity-30" />
                <p className="text-lg font-medium">No documents available</p>
                <p className="text-sm">Upload documents first to select from gallery</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* AI Counselor Uploads */}
                {documents.filter(d => d.source === 'ai_profiler').length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-primary mb-2 flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">AI</Badge>
                      AI Counselor Uploads
                    </p>
                    {documents.filter(d => d.source === 'ai_profiler').map((doc) => {
                      const isAlreadyAssigned = documentTypes.some(dt => doc.document_type_id === dt.id);
                      return (
                        <DocumentHoverPreview key={doc.id} doc={doc}>
                          <div
                            onClick={() => !isAlreadyAssigned && setSelectedGalleryDoc(doc)}
                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all mb-2 ${
                              selectedGalleryDoc?.id === doc.id
                                ? 'border-primary bg-primary/10 ring-2 ring-primary'
                                : isAlreadyAssigned
                                ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                : 'border-gray-200 hover:border-primary/50 hover:bg-primary/5'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                selectedGalleryDoc?.id === doc.id ? 'bg-primary text-white' : 'bg-primary/10 text-primary'
                              }`}>
                                <FileText className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{doc.file_name || doc.filename}</p>
                                <p className="text-xs text-muted-foreground">
                                  {doc.document_type || doc.detected_type || 'Document'} • {doc.file_type}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-primary/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDocumentView(doc.id, doc.file_name || doc.filename);
                                }}
                                title="Open in new tab"
                              >
                                <Eye className="w-4 h-4 text-muted-foreground hover:text-primary" />
                              </Button>
                              {isAlreadyAssigned && (
                                <Badge variant="outline" className="text-xs">Already assigned</Badge>
                              )}
                              {selectedGalleryDoc?.id === doc.id && (
                                <CheckCircle className="w-5 h-5 text-primary" />
                              )}
                            </div>
                          </div>
                        </DocumentHoverPreview>
                      );
                    })}
                  </div>
                )}
                
                {/* Manual/Other Uploads */}
                {documents.filter(d => d.source !== 'ai_profiler').length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Other Uploads
                    </p>
                    {documents.filter(d => d.source !== 'ai_profiler').map((doc) => {
                      const isAlreadyAssigned = documentTypes.some(dt => doc.document_type_id === dt.id);
                      return (
                        <DocumentHoverPreview key={doc.id} doc={doc}>
                          <div
                            onClick={() => !isAlreadyAssigned && setSelectedGalleryDoc(doc)}
                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all mb-2 ${
                              selectedGalleryDoc?.id === doc.id
                                ? 'border-primary bg-primary/10 ring-2 ring-primary'
                                : isAlreadyAssigned
                                ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                : 'border-gray-200 hover:border-primary/50 hover:bg-primary/5'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                selectedGalleryDoc?.id === doc.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
                              }`}>
                                <FileText className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{doc.file_name || doc.filename}</p>
                                <p className="text-xs text-muted-foreground">
                                  {doc.source === 'student_upload' ? 'Student Upload' : 
                                   doc.source === 'web_form' ? 'Web Form' : 
                                   doc.source === 'counselor_scan' ? 'Scanned' : 'Manual Upload'} • {doc.file_type}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-primary/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDocumentView(doc.id, doc.file_name || doc.filename);
                                }}
                                title="Open in new tab"
                              >
                                <Eye className="w-4 h-4 text-muted-foreground hover:text-primary" />
                              </Button>
                              {isAlreadyAssigned && (
                                <Badge variant="outline" className="text-xs">Already assigned</Badge>
                              )}
                              {selectedGalleryDoc?.id === doc.id && (
                                <CheckCircle className="w-5 h-5 text-primary" />
                              )}
                            </div>
                          </div>
                        </DocumentHoverPreview>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDocGalleryOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={attachDocumentFromGallery} 
              disabled={!selectedGalleryDoc || attachingFromGallery}
            >
              {attachingFromGallery ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Attaching...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Attach Selected Document
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace Document Modal */}
      <Dialog open={replaceDocModalOpen} onOpenChange={setReplaceDocModalOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-orange-600" />
              Replace Document
            </DialogTitle>
            <DialogDescription>
              {replaceDocTargetType && (
                <span>
                  Select a different document to replace <strong>{replaceDocCurrent?.file_name || replaceDocCurrent?.filename}</strong> for <strong>{replaceDocTargetType.name}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[55vh] pr-4">
            {/* Current Document */}
            {replaceDocCurrent && (
              <div className="mb-4 p-3 border-2 border-orange-200 bg-orange-50 rounded-lg">
                <p className="text-sm font-medium text-orange-800 mb-2">Currently Assigned:</p>
                <div className="flex items-center gap-3">
                  <div className="w-24 h-24 border rounded-lg overflow-hidden bg-white flex items-center justify-center">
                    {(replaceDocCurrent.file_name || replaceDocCurrent.filename || '').toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|pdf)$/) ? (
                      previewUrls[replaceDocCurrent.id]?.url ? (
                        <img 
                          src={previewUrls[replaceDocCurrent.id].url} 
                          alt="Current document" 
                          className="w-full h-full object-cover"
                        />
                      ) : loadingPreviews[replaceDocCurrent.id] ? (
                        <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                      ) : (
                        <FileText className="w-10 h-10 text-muted-foreground" />
                      )
                    ) : (
                      <FileText className="w-10 h-10 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{replaceDocCurrent.file_name || replaceDocCurrent.filename}</p>
                    <p className="text-sm text-muted-foreground">
                      {replaceDocCurrent.source === 'ai_profiler' ? 'AI Counselor' : 
                       replaceDocCurrent.source === 'student_upload' ? 'Student' : 
                       replaceDocCurrent.source === 'web_form' ? 'Web Form' : 
                       replaceDocCurrent.source === 'zip_upload' ? 'ZIP Upload' : 'Manual Upload'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Available Documents Grid */}
            <div className="mb-2">
              <p className="text-sm font-medium text-muted-foreground">Select a replacement document:</p>
            </div>
            
            {getReplaceableDocuments().length === 0 ? (
              <div className="text-center py-8 bg-muted/20 rounded-lg">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No other documents available for replacement</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {getReplaceableDocuments().map((doc) => {
                  const filename = doc.file_name || doc.filename || '';
                  const isImage = filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/);
                  const isPdf = filename.toLowerCase().endsWith('.pdf');
                  const isSelected = selectedReplaceDoc?.id === doc.id;
                  
                  // Check if already assigned to another slot
                  const assignedToSlot = documentTypes.find(dt => doc.document_type_id === dt.id);
                  
                  return (
                    <div
                      key={doc.id}
                      onClick={() => setSelectedReplaceDoc(doc)}
                      className={`relative cursor-pointer border-2 rounded-lg p-3 transition-all hover:shadow-md ${
                        isSelected 
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {/* Preview */}
                      <div className="w-full h-32 mb-2 rounded-md overflow-hidden bg-muted/30 flex items-center justify-center">
                        {(isImage || isPdf) ? (
                          previewUrls[doc.id]?.url ? (
                            <img 
                              src={previewUrls[doc.id].url} 
                              alt={filename}
                              className="w-full h-full object-contain"
                            />
                          ) : previewUrls[doc.id]?.failed ? (
                            <div className="text-center">
                              <FileText className={`w-12 h-12 mx-auto ${isPdf ? 'text-red-500' : 'text-blue-400'} mb-1`} />
                              <span className="text-xs text-muted-foreground">{isPdf ? 'PDF' : 'Image'}</span>
                            </div>
                          ) : loadingPreviews[doc.id] ? (
                            <div className="text-center">
                              <Loader2 className="w-8 h-8 mx-auto text-muted-foreground animate-spin mb-1" />
                              <span className="text-xs text-muted-foreground">Loading...</span>
                            </div>
                          ) : (
                            <div className="text-center">
                              <FileText className={`w-12 h-12 mx-auto ${isPdf ? 'text-red-500' : 'text-blue-400'} mb-1`} />
                              <span className="text-xs text-muted-foreground">{isPdf ? 'PDF' : 'Image'}</span>
                            </div>
                          )
                        ) : (
                          <div className="text-center">
                            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-1" />
                            <span className="text-xs text-muted-foreground">{filename.split('.').pop()?.toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Document Info */}
                      <div className="space-y-1">
                        <p className="text-sm font-medium truncate" title={filename}>
                          {filename || 'Unnamed Document'}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="outline" className="text-xs">
                            {doc.source === 'ai_profiler' ? 'AI' : 
                             doc.source === 'student_upload' ? 'Student' : 
                             doc.source === 'web_form' ? 'Web' : 
                             doc.source === 'zip_upload' ? 'ZIP' : 'Upload'}
                          </Badge>
                          {assignedToSlot && (
                            <Badge variant="secondary" className="text-xs">
                              {assignedToSlot.name.substring(0, 15)}...
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Selection Indicator */}
                      {isSelected && (
                        <div className="absolute top-2 right-2">
                          <CheckCircle className="w-6 h-6 text-primary fill-primary/20" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter className="mt-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {selectedReplaceDoc && (
                <span>Selected: <strong>{selectedReplaceDoc.file_name || selectedReplaceDoc.filename}</strong></span>
              )}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setReplaceDocModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={replaceDocument} 
                disabled={!selectedReplaceDoc || replacingDocument}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {replacingDocument ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Replacing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Replace Document
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicationDetailPage;
